import React, { useEffect, useCallback, useContext } from 'react';
import PT from 'prop-types';
import cn from 'classnames';
import { NavLink, useParams } from 'react-router-dom';
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import { Scrollbars } from 'react-custom-scrollbars';

import { queryGetProfileData } from '../../screens/Profile/Profile';
import Input from '../Input/Input';
import NavbarItemLink from './components/NavbarItemLink';
import NavbarList from './components/NavbarList';
import Logo from './logo.svg';
import LogoMobile from './Logo_full.svg';
import ProfilePreview from '../ProfilePreview';
import useReducedErrors from 'Shared/hooks/useReducedErrors';
import StoreContext from 'Shared/context/StoreContext';
import moment from 'moment';


export const queryGetContractsData = gql`
  query ContractsDataQuery @api(name: root) {
    contracts {
      id
      title
    }
  }
`;

const removeMobileBuildsItem = (points) => {
  return points.filter((point) => point[0].id !== 'mobile-builds');
};

const Navbar = (props) => {
  const { activePointId, points = [], className } = props;
  const { contract, setContract } = useContext(StoreContext);

  const { id } = useParams();
  const currentActivePointId = activePointId || id;

  const formatContractsForSelect = ({ contracts }) =>
    contracts
      .filter((contract) => !!contract)
      .map((item) => {
        return item && { id: item.id, label: item.title, value: item.title };
      });

  const onContractChange = useCallback(
    (contractSelected) => {
      setContract(contractSelected);
    },
    [setContract]
  );

  const reducedErrors = useReducedErrors({});

  const toggleMenu = () => {
    const status = document.body.classList.toggle('toggle-menu');
    window.localStorage.setItem('isNavbarOpen', !status);
  };

  useEffect(() => {
    const status = window.localStorage.getItem('isNavbarOpen') || true;
    window.localStorage.setItem('isNavbarOpen', status);
    if (!JSON.parse(status)) {
      document.body.classList.add('toggle-menu');
    }
  }, []);

  const { data } = useQuery(queryGetProfileData, {
    options: { cache: 'cache-only' },
  });

  const [getContracts, { data: dataContracts }] = useLazyQuery(queryGetContractsData);

  useEffect(() => {
    data && data.user.customerprofile && getContracts();
  }, [data, getContracts]);

  useEffect(() => {
    !contract && dataContracts && dataContracts.contracts && setContract(formatContractsForSelect(dataContracts)[0]);
  }, [contract, dataContracts, setContract]);

  const updatedMenuItems =
    data && data.user.employeeprofile && data.user.employeeprofile.mobileProjects.length > 0
      ? points
      : removeMobileBuildsItem(points);

  return (
    <div className={cn('navbar', className)}>
      <ProfilePreview />
      <span className="navbar__overlay" onClick={toggleMenu} />
      <div className="fix-navbar-holder">
        <Scrollbars
          autoHide
          autoHideTimeout={1000}
          autoHideDuration={200}
          autoHeight
          autoHeightMin={0}
          autoHeightMax={100 + 'vh'}
          thumbMinSize={30}
          universal={true}
        >
          <div className="navbar__holder">
            <div className="navbar__wrap">
              <div>
                <strong className="navbar__logo">
                  <NavLink
                    to={
                      data && data.user.customerprofile
                        ? '/customer/invoices/open/'
                        : '/employee/vacations/leave-request/'
                    }
                  >
                    <img
                      className="navbar__logo-img hidden-xs"
                      src={(data && data.user.config.logo) || Logo}
                      alt="anvileight"
                    />
                    <img
                      className="navbar__logo-img visible-xs"
                      src={(data && data.user.config.logo) || LogoMobile}
                      alt="anvileight"
                    />
                  </NavLink>
                </strong>
                <span className="navbar__menu-toggle navbar__menu-close" onClick={toggleMenu}>
                  toggle menu
                </span>
                <nav className="navbar__groups">
                  {updatedMenuItems.map((group, groupIndex) => (
                    <NavbarList key={groupIndex}>
                      <>
                        {!groupIndex && dataContracts && (
                          <div className="contracts-select">
                            <Input
                              label=""
                              variant="select"
                              helper={reducedErrors['contract']}
                              helperVariant={reducedErrors['contract'] ? 'error' : 'message'}
                              callback={onContractChange}
                              options={formatContractsForSelect(dataContracts)}
                              fieldProps={{
                                value: contract || formatContractsForSelect(dataContracts)[0],
                                name: 'contract',
                                placeholder: 'Select your contract',
                                isSearchable: true,
                              }}
                            />
                          </div>
                        )}
                        {group.map((point) => (
                          <NavbarItemLink
                            key={point.id}
                            point={point}
                            active={currentActivePointId === point.id}
                            isMobileProjectItem={point.id === 'mobile-builds' && id}
                          />
                        ))}
                      </>
                    </NavbarList>
                  ))}
                </nav>
              </div>
              <span className="navbar__copyright">© Copyright {moment().format('YYYY')} AnvilEight LTD</span>
            </div>
          </div>
        </Scrollbars>
      </div>
      <span className="navbar__menu-toggle navbar__menu-open" onClick={toggleMenu}>
        toggle menu
      </span>
    </div>
  );
};

Navbar.propTypes = {
  activePointId: PT.oneOfType([PT.string, PT.number]),
  points: PT.arrayOf(PT.arrayOf(PT.shape(NavbarItemLink.propTypes))),
  className: PT.string,
};

export default Navbar;
